/**
 * PageSearchBar — the standard page-level search input used throughout the
 * ACS Command Center. Every page-level search bar must use this component so
 * placement, height, and visual treatment are identical.
 *
 * Locked by Tom Clancy on 2026-05-14. Do not fork visually — extend props
 * if a new behavior is needed.
 *
 * NOTE: this is NOT the global top-bar search (see Layout.js). Use this only
 * inside a page's filter row.
 */
import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

const PageSearchBar = ({
  value,
  onChange,
  placeholder = 'Search…',
  width = 360,
  autoFocus = false,
  onClear,
  sx = {},
  ...rest
}) => {
  const handleChange = (e) => {
    if (typeof onChange === 'function') {
      onChange(e.target.value);
    }
  };

  const handleClear = () => {
    if (typeof onClear === 'function') {
      onClear();
    } else if (typeof onChange === 'function') {
      onChange('');
    }
  };

  const hasValue = value !== undefined && value !== null && String(value).length > 0;

  return (
    <Box sx={{ width: { xs: '100%', sm: width }, m: 0, ...sx }}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        value={value ?? ''}
        onChange={handleChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: hasValue ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                edge="end"
                aria-label="clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        {...rest}
      />
    </Box>
  );
};

export default PageSearchBar;
